import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { atom } from "xoid";
import { useAtom } from "@xoid/react";
import { z } from "zod";
import type { KioskAudioSlotKey } from "@common/kiosk-audio/slots";
import { useLanguage } from "@/hooks/use-language";
import { getShopFromLocalStorage } from "@/hooks/use-shop";
import { getToken } from "@/hooks/use-token";
import { useUser } from "@/hooks/use-user";
import { getCashAgentUrl, getServerUrl, useFeatureFlags } from "@/lib/runtime-config";
import {
  createKioskAudioController,
  type KioskAudioController,
} from "./kiosk-audio-controller";

const manifestSchema = z.object({
  clips: z.array(z.object({ slot: z.string(), lang: z.string(), version: z.coerce.string() })),
});

export const kioskAudioManifestQueryKey = ["kiosk-audio-manifest"];

const MANIFEST_REFETCH_MS = 5 * 60 * 1000;
const SESSION_MUTED_KEY = "kiosk-audio-muted";

export const kioskAudioController: KioskAudioController = createKioskAudioController({
  clipUrl: (clip) =>
    `${getCashAgentUrl()}/kiosk-audio/${encodeURIComponent(clip.slot)}/${clip.lang}?v=${encodeURIComponent(clip.version)}`,
});

async function fetchManifest() {
  const response = await fetch(`${getCashAgentUrl()}/kiosk-audio/manifest`);
  if (!response.ok) {
    throw new Error(`kiosk audio manifest: ${response.status}`);
  }
  return manifestSchema.parse(await response.json()).clips;
}

/**
 * Feeds the playback controller: the `kiosk.audio` flag, the language, the
 * mute state and the clip manifest from cash-agent. Render once, in the root.
 */
export function KioskAudio({
  controller = kioskAudioController,
}: {
  controller?: KioskAudioController;
}) {
  const enabled = useFeatureFlags().audio;
  const language = useLanguage();
  const { muted } = useKioskAudioMuted();
  const manifest = useQuery({
    queryKey: kioskAudioManifestQueryKey,
    queryFn: fetchManifest,
    enabled,
    refetchInterval: MANIFEST_REFETCH_MS,
    retry: false,
  });

  // Effects sync an external system (the shared Audio element); order matters:
  // language and mute first, so enabling or a fresh manifest plays the right clip.
  useEffect(() => controller.setLanguage(language), [controller, language]);
  useEffect(() => controller.setMuted(muted), [controller, muted]);
  useEffect(() => controller.setClips(manifest.data ?? []), [controller, manifest.data]);
  useEffect(() => controller.setEnabled(enabled), [controller, enabled]);

  return null;
}

/** Declare the audio slot this component shows while it is mounted. */
export function useAudioSlot(slot: KioskAudioSlotKey, controller: KioskAudioController = kioskAudioController) {
  useEffect(() => controller.mountSlot(slot), [controller, slot]);
}

/** JSX form of `useAudioSlot`, for inline modals and conditional branches. */
export function AudioSlot({ slot, controller }: { slot: KioskAudioSlotKey; controller?: KioskAudioController }) {
  useAudioSlot(slot, controller);
  return null;
}

// Bumped on every toggle so readers of sessionStorage re-render.
const sessionMuteVersion = atom(0);

function readSessionMuted(): boolean | null {
  const value = sessionStorage.getItem(SESSION_MUTED_KEY);
  return value === null ? null : value === "true";
}

/**
 * Effective mute: a toggle made in this session wins, then the logged-in
 * user's saved preference, else unmuted. The session toggle lives in
 * sessionStorage, which the start screen clears.
 */
export function useKioskAudioMuted() {
  useAtom(sessionMuteVersion);
  const { data: user } = useUser();
  const sessionMuted = readSessionMuted();
  return {
    muted: sessionMuted ?? user?.kioskAudioMuted ?? false,
    isLoggedIn: !!user,
  };
}

/** Toggle mute for this session; logged-in users also get it saved on their account. */
export function useToggleKioskAudioMuted() {
  const { muted, isLoggedIn } = useKioskAudioMuted();
  const saveMuted = useMutation({
    mutationFn: async (kioskAudioMuted: boolean) => {
      const response = await fetch(`${getServerUrl()}/users`, {
        method: "PATCH",
        body: JSON.stringify({ kioskAudioMuted }),
        headers: {
          "Kiosk-Key": getShopFromLocalStorage()?.kioskKey ?? "",
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Saving kiosk audio mute failed: ${response.status}`);
      }
    },
    // Offline or failed: the session toggle still applies; no outbox retry.
    onError: (error) => console.warn(error),
  });

  return () => {
    const next = !muted;
    sessionStorage.setItem(SESSION_MUTED_KEY, String(next));
    sessionMuteVersion.update((version) => version + 1);
    if (isLoggedIn) {
      saveMuted.mutate(next);
    }
  };
}
